import type { Member } from '@stytch/nextjs/b2b'
import { applyPostAuthBase, type PostAuthDependencies } from '@/utilities/auth/authHelper'
import { runClientPostAuth } from '@/utilities/auth/runClientPostAuth'
import { determinePostLoginRedirect } from '@/utilities/auth/determinePostLoginRedirect'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { getStytchJWT } from '@/utilities/stytch/getStytchJWT'
import { getAuthMethodFromStytchPayload } from '@/utilities/shared/stytchSession'
import {
  readAndClearRedirectIntent,
  redactSensitivePath,
  resolvePatientPostAuthDestination,
} from '@/utilities/redirectHelpers'
import { syncTenantPartnerIds } from '@/utilities/syncTenantPartnerId'
import { clearGoogleOneTapSkip } from '@/utilities/stytch/googleOneTap'
import userService from '@/services/patient/user'

export type FinishAuthenticatedSessionOptions = {
  allowClientSignup?: boolean
}

export async function finishAuthenticatedSession(
  member: Member,
  orgSlug: string,
  searchParams: URLSearchParams,
  isClient: boolean,
  deps: PostAuthDependencies,
  options?: FinishAuthenticatedSessionOptions,
): Promise<string> {
  const clientPortal = orgSlug === 'client' || isClient
  if (clientPortal) {
    const dest = await runClientPostAuth(member, orgSlug, deps, {
      allowSignup: options?.allowClientSignup ?? true,
    })
    clearGoogleOneTapSkip()
    return readAndClearRedirectIntent(searchParams, 'client') ?? dest
  }

  applyPostAuthBase(member, orgSlug, deps)
  const { redirectUrl, isFirstTimeUser } = await determinePostLoginRedirect({
    email: member.email_address,
  })
  const redirectIntent = readAndClearRedirectIntent(searchParams, 'patient')
  const finalDest = resolvePatientPostAuthDestination({
    redirectUrl,
    isFirstTimeUser,
    redirectIntent,
  })

  const sessionPayload = getStytchJWT()

  trackEvent('login:success', {
    firstTimeUser: isFirstTimeUser,
    redirectUrl: redactSensitivePath(finalDest),
    authMethod: getAuthMethodFromStytchPayload(sessionPayload),
  })
  clearGoogleOneTapSkip()
  // Always invoke these side effects. Impersonation no-ops belong in
  // syncPartnerIdsAction and /api/hipaa-session-init after JWKS validation —
  // do not skip from an unsigned, browser-decoded session JWT.
  if (isFirstTimeUser === false) {
    await syncTenantPartnerIds({
      context: 'existing-user-authentication',
    })
  }

  await userService.updateHipaaConsentPendingClaim(true)

  return finalDest
}
